import React, { Component } from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { debounce } from "lodash";
import { Grid } from "semantic-ui-react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import ContentLoader from "~/components/element/ContentLoader";
import RouteWrapper from "../../spa/component/wrapper/RouteWrapper";
import PaginatedTable from "~/components/PaginatedTable";
import TourIndexList from "../components/TourIndexList";
import TourIndexHeader from "../components/TourIndexHeader";
import TourIndexStore from "../stores/TourIndexStore";
import GroupCustomerStore from "~/stores/GroupCustomerStore";
import TourClientFacingStatus from "~/constants/Tour/TourClientFacingStatus";
import TourBulkActionMenu from "~/modules/PanelCommon/components/TourBulkActionMenu";
import ToastMessageStore from "~/stores/ToastMessageStore";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import TourBulkUpdateStore from "~/stores/Tour/TourBulkUpdateStore";
import "../styles/TourIndexPage.less";

@observer
class TourIndexPage extends Component {
  /**
   * @property {GroupCustomerStore}
   */
  groupCustomerStore = null;

  /**
   * @property {TourBulkUpdateStore|null}
   */
  tourBulkUpdateStore = null;

  @observable selectedTours = [];

  constructor(props) {
    super(props);

    this.groupCustomerStore = new GroupCustomerStore();

    this.tourBulkUpdateStore = new TourBulkUpdateStore();

    this.state = {
      showTourNotAccessibleModal: false,
      tempKeywordString: "",
    };
    document.title = "Tours";
    const { tourIndexStore } = this.props;
    tourIndexStore.pagination.currentPage = 1;
    this.changeKeywordString = debounce(this.changeKeywordString.bind(this), 500, { leading: false, trailing: true });
  }

  componentDidMount() {
    const { showError, tourIndexStore, query, location, redirectTo } = this.props;
    const { searchParams } = tourIndexStore;
    const { keywordString } = searchParams;

    // If the url has specific hash it comes from the old client panel url
    // We need to redirect
    // Example of hash: #/tours/85/edit-tour
    let oldClientPanelHash = location.hash;

    if (oldClientPanelHash.startsWith("#/tours/")) {
      oldClientPanelHash = oldClientPanelHash.slice(8);
      const parts = oldClientPanelHash.split("/");

      // Go to the main tour page
      if (parts.length === 1) {
        redirectTo(redirectTo(`/tours/${parts[0]}`));
      } else if (parts.length === 2) {
        switch (parts[1]) {
          case "edit-tour":
            redirectTo(redirectTo(`/tours/${parts[0]}`));
            break;
          case "downloads":
            redirectTo(redirectTo(`/tours/${parts[0]}/download`));
            break;
          case "video":
            redirectTo(redirectTo(`/tours/${parts[0]}/video-request`));
            break;
          default:
            // flyer, stats, etc.
            redirectTo(redirectTo(`/tours/${parts[0]}/${parts[1]}`));
        }
      }
    }

    if (query.page) {
      const pageNumber = parseInt(query.page, 10);
      tourIndexStore.pagination.currentPage = pageNumber;
    }

    this.setState({ tempKeywordString: keywordString });
    this.groupCustomerStore.load().catch(error => showError(error.message));
    tourIndexStore.loadIndex().catch(error => showError(error.message));
  }

  onBulkUpdateIsActive(isActive) {
    if (this.selectedTours.length === 0) {
      return Promise.resolve();
    }

    const { tourIndexStore, showError, toastMessageStore, t } = this.props;

    const data = { tours: [] };

    this.selectedTours.forEach(selectedTour => {
      data.tours.push({
        tourId: selectedTour.tourId,
        isActive,
      });
    });

    return this.tourBulkUpdateStore
      .update(data)
      .then(() => {
        this.setSelectedTours([]);
        toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
        tourIndexStore.loadIndex().catch(error => showError(error.message));
      })
      .catch(error => showError(error.message));
  }

  setSelectedTours(selectedTours) {
    this.selectedTours = selectedTours;
  }

  selectTour(tourId, customerHasUrbanimmersiveGroupSpecialPricing) {
    if (this.selectedTours.findIndex(item => item.tourId === tourId) === -1) {
      this.selectedTours.push({
        tourId,
        customerHasUrbanimmersiveGroupSpecialPricing,
      });
    }
  }

  unselectTour(tourId) {
    const foundIndex = this.selectedTours.findIndex(item => item.tourId === tourId);
    if (foundIndex > -1) {
      this.selectedTours.splice(foundIndex, 1);
    }
  }

  closeTourNotAccessibleModal() {
    this.setState({ showTourNotAccessibleModal: false });
  }

  openTourNotAccessibleModal() {
    this.setState({ showTourNotAccessibleModal: true });
  }

  openTour(tour) {
    const { redirectTo } = this.props;

    if (
      tour.clientFacingStatus === TourClientFacingStatus.PUBLISHED ||
      (tour.customerHasUrbanimmersiveGroupSpecialPricing && tour.clientFacingStatus === TourClientFacingStatus.INACTIVE)
    ) {
      redirectTo(`/tours/${tour.tourId}`);
    } else if (tour.clientFacingStatus === TourClientFacingStatus.AWAITING_PAYMENT) {
      redirectTo(`/tours/${tour.tourId}/review`);
    } else if (
      tour.clientFacingStatus === TourClientFacingStatus.PHOTOS_READY ||
      tour.clientFacingStatus === TourClientFacingStatus.ARCHIVED
    ) {
      redirectTo(`/tours/${tour.tourId}/download`);
    } else {
      this.openTourNotAccessibleModal();
    }
  }

  changeDropdown(data) {
    const { tourIndexStore, showError } = this.props;
    tourIndexStore.searchParams.filterValue = data.value;
    tourIndexStore.pagination.currentPage = 1;
    tourIndexStore.loadIndex().catch(error => showError(error.message));
  }

  changeTempKeywordString(data) {
    this.setState({ tempKeywordString: data.value });

    this.changeKeywordString();
  }

  changeKeywordString() {
    const { showError, tourIndexStore } = this.props;
    const { tempKeywordString } = this.state;
    tourIndexStore.pagination.currentPage = 1;
    tourIndexStore.searchParams.keywordString = tempKeywordString;
    tourIndexStore.loadIndex().catch(error => showError(error.message));
  }

  changeToursByCustomerOrCoCustomerId(data) {
    const { tourIndexStore, showError } = this.props;
    tourIndexStore.searchParams.customerOrCoCustomerId = data.value;
    tourIndexStore.pagination.currentPage = 1;
    tourIndexStore.loadIndex().catch(error => showError(error.message));
  }

  clearSearch() {
    const { tourIndexStore, showError } = this.props;

    tourIndexStore.searchParams.filterValue = "0";
    tourIndexStore.searchParams.keywordString = "";
    tourIndexStore.searchParams.customerId = "0";
    this.setState({ tempKeywordString: "" });
    tourIndexStore.loadIndex().catch(error => showError(error.message));
  }

  render() {
    const { tourIndexStore, showError, replaceQuery, toastMessageStore, t } = this.props;
    const { showTourNotAccessibleModal, tempKeywordString } = this.state;
    const {
      tours,
      pagination,
      searchParams,
      statusFilterDropdownLabel,
      hasSelectedFilters,
      isLoading,
    } = tourIndexStore;
    const { groupCustomers } = this.groupCustomerStore;
    const { filterValue, keywordString, customerOrCoCustomerId } = searchParams;
    return (
      <>
        <LoadingWrapper loading={this.tourBulkUpdateStore.isSaving} message={t("saving")} />
        <div className="tour-index-page-container">
          <TourIndexHeader
            statusFilterDropdownLabel={statusFilterDropdownLabel}
            hasSelectedFilters={hasSelectedFilters}
            tempKeywordString={tempKeywordString}
            filterValue={filterValue}
            keywordString={keywordString}
            clearSearch={() => this.clearSearch()}
            changeTempKeywordString={(_event, data) => this.changeTempKeywordString(data)}
            changeDropdown={(_event, data) => this.changeDropdown(data)}
            groupCustomers={groupCustomers}
            customerOrCoCustomerId={customerOrCoCustomerId}
            changeToursByCustomerOrCoCustomerId={(_event, data) => this.changeToursByCustomerOrCoCustomerId(data)}
          />

          <TourBulkActionMenu
            selectedTours={this.selectedTours}
            onBulkUpdateIsActiveCallback={isActive => this.onBulkUpdateIsActive(isActive)}
            validationErrorMessages={this.tourBulkUpdateStore.errorMessages}
          />

          <Grid centered className="main-grid">
            <Grid.Column>
              {isLoading ? (
                <ContentLoader />
              ) : (
                <PaginatedTable
                  pagination={pagination}
                  changePageCallback={page => {
                    tourIndexStore.changePage(page).catch(error => showError(error.message));
                    replaceQuery({ page });
                  }}
                  totalItemsCount={tours.length}
                >
                  <TourIndexList
                    tours={tours}
                    openTourCallback={tour => this.openTour(tour)}
                    showTourNotAccessibleModal={showTourNotAccessibleModal}
                    closeTourNotAccessibleModalCallback={() => this.closeTourNotAccessibleModal()}
                    clearSearch={() => this.clearSearch()}
                    selectedTours={this.selectedTours}
                    selectTourCallback={(tourId, customerHasUrbanimmersiveGroupSpecialPricing) =>
                      this.selectTour(tourId, customerHasUrbanimmersiveGroupSpecialPricing)
                    }
                    unselectTourCallback={tourId => this.unselectTour(tourId)}
                  />
                </PaginatedTable>
              )}
            </Grid.Column>
          </Grid>
        </div>
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

TourIndexPage.propTypes = {
  t: PropTypes.func.isRequired,
  tourIndexStore: PropTypes.instanceOf(TourIndexStore),
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
  location: PropTypes.shape({
    hash: PropTypes.string.isRequired,
  }).isRequired,
};

export default ErrorWrapper(RouteWrapper(withTranslation()(TourIndexPage)));
